import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Link, useMatch } from '@tanstack/react-router';
import { useLocation, useParams } from '@ui/app-router';
import { STORAGE_KEYS } from '@ui/constants';
import { LogoMobileZephyrIcon } from '@ui/custom-icons';
import { SidebarProvider, SidebarTrigger } from '@ui/primitives/sidebar';
import { ProductAnalyticsEvent, trackProductAnalyticsEvent } from '@ui/utils';
import { useEffect, useMemo, useRef, useState } from 'react';
import { navigationItemsAppLevel, navigationItemsAuthenticatedUserProfileLevel, navigationItemsOrgLevel, navigationItemsProfileLevel, navigationItemsProjectLevel } from '../../navigation-items';
import { Header } from './_elements/header';
import { AppSidebar } from './app-sidebar';
import { AppBreadcrumb } from './breadcrumb';
import { CreateOrgModalProvider } from './create-org-modal';
import { EnvironmentLogo } from './environment-logo';
import { FeedbackButton } from './feedback-button';
import { HeaderHelpMenu } from './header-help-menu';
import { HeaderUserMenu } from './header-user-menu';
import { MobileMenu, MobileMenuTrigger } from './mobile-menu';
import { SearchModal, SidebarSearch, useLocalStorage } from './sidebar';
// Routes that render standalone (no header/sidebar shell)
const STANDALONE_ROUTES = [
    '/auth/verify-email',
    '/logout',
    '/login'
];
const Layout = (param)=>{
    let { children } = param;
    var _orgMatch_loaderData, _projectMatch_loaderData, _appMatch_loaderData;
    const { organization, project, application, username: user } = useParams({
        strict: false
    });
    const location = useLocation();
    const isStandalone = STANDALONE_ROUTES.some((route)=>location.pathname.startsWith(route));
    if (isStandalone) {
        return /*#__PURE__*/ _jsx(_Fragment, {
            children: children
        });
    }
    let orgMatch, projectMatch, appMatch;
    try {
        orgMatch = useMatch({
            from: '/org/$organization',
            shouldThrow: false
        });
        projectMatch = useMatch({
            from: '/org/$organization/$project',
            shouldThrow: false
        });
        appMatch = useMatch({
            from: '/org/$organization/$project/app/$application',
            shouldThrow: false
        });
    } catch (e) {
        //eslint-disable-next-line no-console
        console.error('Error matching routes:', e);
    }
    const orgPermissions = orgMatch === null || orgMatch === void 0 ? void 0 : (_orgMatch_loaderData = orgMatch.loaderData) === null || _orgMatch_loaderData === void 0 ? void 0 : _orgMatch_loaderData.permissions;
    const projectPermissions = projectMatch === null || projectMatch === void 0 ? void 0 : (_projectMatch_loaderData = projectMatch.loaderData) === null || _projectMatch_loaderData === void 0 ? void 0 : _projectMatch_loaderData.permissions;
    const appPermissions = appMatch === null || appMatch === void 0 ? void 0 : (_appMatch_loaderData = appMatch.loaderData) === null || _appMatch_loaderData === void 0 ? void 0 : _appMatch_loaderData.permissions;
    const [searchModalOpen, setSearchModalOpen] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const searchButtonRef = useRef(null);
    const [isSidebarOpen, setIsSidebarOpen] = useLocalStorage(STORAGE_KEYS.IS_SIDEBAR_OPEN, true);
    const headerRef = useRef(null);
    const layoutRef = useRef(null);
    const isProfileRoute = useMemo(()=>{
        const pathname = location.pathname;
        if (!pathname.startsWith('/profile')) return false;
        const segments = pathname.split('/').filter(Boolean);
        const second = segments[1];
        // True for /profile, /profile/, /profile/settings/*.
        // False when visiting another user's profile (/profile/$username).
        return !(second && second !== 'settings');
    }, [
        location.pathname
    ]);
    useEffect(()=>{
        if (!organization || typeof window === 'undefined') {
            return;
        }
        try {
            localStorage.setItem(STORAGE_KEYS.LAST_VISITED_ORG, organization);
        } catch (error) {
            //eslint-disable-next-line no-console
            console.error('Error updating localStorage:', error);
        }
    }, [
        organization
    ]);
    // Update CSS custom properties based on scroll position for smooth scroll-linked animation
    useEffect(()=>{
        let rafId = null;
        // Read CSS variables once on mount
        const styles = getComputedStyle(document.documentElement);
        const row1Height = parseInt(styles.getPropertyValue('--header-row-1-height'));
        const row2Height = parseInt(styles.getPropertyValue('--header-row-2-height'));
        const maxScroll = parseInt(styles.getPropertyValue('--header-scroll-range'));
        const navOffset = parseInt(styles.getPropertyValue('--header-nav-offset'));
        // Calculate heights from row heights (can't use calc() values directly)
        const expandedHeight = row1Height + row2Height;
        const heightDiff = row1Height;
        const handleScroll = ()=>{
            if (rafId !== null) return;
            rafId = requestAnimationFrame(()=>{
                const scrollY = window.scrollY;
                // Calculate progress from 0 to 1 based on scroll position
                const progress = Math.max(0, Math.min(1, scrollY / maxScroll));
                // Calculate actual pixel values with decimal precision for smooth animation
                // Keep 2 decimal places to avoid excessive precision while maintaining smoothness
                const height = Math.round((expandedHeight - progress * heightDiff) * 100) / 100;
                const translateY = Math.round(progress * -row1Height * 100) / 100;
                const translateX = Math.round(progress * navOffset * 100) / 100;
                // Calculate negative margin to prevent content push - when expanded (96px), we need -48px margin
                const marginBottom = Math.round((progress * heightDiff - heightDiff) * 100) / 100;
                // Update CSS variables directly - browser handles sub-pixel rendering
                if (headerRef.current) {
                    headerRef.current.style.setProperty('--header-height', "".concat(height.toString(), "px"));
                    headerRef.current.style.setProperty('--header-margin-bottom', "".concat(marginBottom.toString(), "px"));
                    headerRef.current.style.setProperty('--translate-y', "".concat(translateY.toString(), "px"));
                    headerRef.current.style.setProperty('--translate-x', "".concat(translateX.toString(), "px"));
                }
                // Update layout ref so sidebar can access dynamic header height
                if (layoutRef.current) {
                    layoutRef.current.style.setProperty('--header-height', "".concat(height.toString(), "px"));
                }
                rafId = null;
            });
        };
        // Set initial value
        handleScroll();
        window.addEventListener('scroll', handleScroll, {
            passive: true
        });
        return ()=>{
            window.removeEventListener('scroll', handleScroll);
            if (rafId !== null) {
                cancelAnimationFrame(rafId);
            }
        };
    }, []);
    // Auto-collapse sidebar on mobile/tablet screens
    useEffect(()=>{
        const handleResize = ()=>{
            const isLargeScreen = window.innerWidth >= 1024; // lg breakpoint
            if (!isLargeScreen && isSidebarOpen) {
                setIsSidebarOpen(false);
            }
        };
        // Check on mount
        handleResize();
        window.addEventListener('resize', handleResize);
        return ()=>{
            window.removeEventListener('resize', handleResize);
        };
    }, [
        isSidebarOpen,
        setIsSidebarOpen
    ]);
    useEffect(()=>{
        const handleKeyDown = (event)=>{
            if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
                event.preventDefault();
                event.stopPropagation();
                trackProductAnalyticsEvent(ProductAnalyticsEvent.CommandPaletteOpened, {
                    trigger: 'keyboard',
                    key_combo: event.metaKey ? 'cmd+k' : 'ctrl+k',
                    pathname: location.pathname,
                    workspace_organization: organization,
                    workspace_project: project,
                    workspace_application: application
                });
                setSearchModalOpen(true);
                return;
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return ()=>{
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [
        application,
        location.pathname,
        organization,
        project
    ]);
    // Determine navigation level based on route params - most specific first
    const getNavigationItems = useMemo(()=>{
        // Application level - most specific
        if (application) {
            return (params)=>navigationItemsAppLevel(params);
        }
        // Project level
        if (project) {
            return (params)=>navigationItemsProjectLevel(params);
        }
        // Organization level
        if (organization) {
            return (params)=>navigationItemsOrgLevel(params);
        }
        // // Profile level - check if viewing own profile
        if (isProfileRoute) {
            // TODO: Determine if authenticated user vs other user profile
            // For now, assume authenticated user profile
            return ()=>navigationItemsAuthenticatedUserProfileLevel();
        }
        if (user) {
            return (params)=>navigationItemsProfileLevel(params);
        }
        // No navigation
        return ()=>[];
    }, [
        organization,
        project,
        application,
        user,
        isProfileRoute
    ]);
    const navigationItems = useMemo(()=>{
        var _ref, _ref1, _ref2, _ref3;
        var _orgPermissions_org, _projectPermissions_project, _appPermissions_app;
        return getNavigationItems({
            organization: {
                name: organization !== null && organization !== void 0 ? organization : '',
                settingsAvailable: (_ref = orgPermissions === null || orgPermissions === void 0 ? void 0 : (_orgPermissions_org = orgPermissions.org) === null || _orgPermissions_org === void 0 ? void 0 : _orgPermissions_org.updateOrganizationSettings) !== null && _ref !== void 0 ? _ref : false
            },
            project: {
                name: project !== null && project !== void 0 ? project : '',
                settingsAvailable: (_ref1 = projectPermissions === null || projectPermissions === void 0 ? void 0 : (_projectPermissions_project = projectPermissions.project) === null || _projectPermissions_project === void 0 ? void 0 : _projectPermissions_project.updateProjectSettings) !== null && _ref1 !== void 0 ? _ref1 : false
            },
            application: {
                name: application !== null && application !== void 0 ? application : '',
                settingsAvailable: (_ref2 = appPermissions === null || appPermissions === void 0 ? void 0 : (_appPermissions_app = appPermissions.app) === null || _appPermissions_app === void 0 ? void 0 : _appPermissions_app.updateApplicationSettings) !== null && _ref2 !== void 0 ? _ref2 : false,
                devopsAvailable: (_ref3 = appPermissions === null || appPermissions === void 0 ? void 0 : appPermissions.isDevopsAllowed) !== null && _ref3 !== void 0 ? _ref3 : false
            },
            user: {
                name: user !== null && user !== void 0 ? user : ''
            }
        });
    }, [
        application,
        getNavigationItems,
        organization,
        project,
        user,
        orgPermissions,
        projectPermissions,
        appPermissions
    ]);
    return /*#__PURE__*/ _jsx(CreateOrgModalProvider, {
        children: /*#__PURE__*/ _jsx(SidebarProvider, {
            open: isSidebarOpen,
            onOpenChange: setIsSidebarOpen,
            className: "h-full w-full flex flex-col",
            children: /*#__PURE__*/ _jsxs("div", {
                ref: layoutRef,
                className: "flex flex-col h-full w-full min-w-0",
                children: [
                    /*#__PURE__*/ _jsxs("header", {
                        ref: headerRef,
                        className: "sticky top-0 z-50",
                        "data-e2e": "LAYOUT_HEADER",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "flex lg:hidden justify-between items-center h-12 px-3 w-full",
                                children: /*#__PURE__*/ _jsxs("div", {
                                    className: "flex items-center gap-2",
                                    children: [
                                        /*#__PURE__*/ _jsx(MobileMenuTrigger, {
                                            onClick: ()=>{
                                                setMobileMenuOpen(true);
                                            }
                                        }),
                                        /*#__PURE__*/ _jsx(Link, {
                                            to: '/',
                                            className: "flex items-center",
                                            children: /*#__PURE__*/ _jsx(LogoMobileZephyrIcon, {})
                                        })
                                    ]
                                })
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "hidden lg:block overflow-hidden",
                                style: {
                                    height: 'var(--header-height)',
                                    marginBottom: 'var(--header-margin-bottom)',
                                    willChange: 'height, margin-bottom'
                                },
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "bg-sidebar flex justify-between items-center",
                                        style: {
                                            height: 'var(--header-row-1-height)'
                                        },
                                        children: [
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: "flex items-center px-6 gap-2",
                                                children: [
                                                    /*#__PURE__*/ _jsx("div", {
                                                        className: "z-20 animate-transform",
                                                        children: /*#__PURE__*/ _jsx(Link, {
                                                            to: '/',
                                                            "data-e2e": "LAYOUT_HEADER__LOGO",
                                                            children: /*#__PURE__*/ _jsx(EnvironmentLogo, {})
                                                        })
                                                    }),
                                                    /*#__PURE__*/ _jsxs("div", {
                                                        className: "flex items-center",
                                                        style: {
                                                            transform: 'translate3d(0, var(--translate-y), 0)',
                                                            willChange: 'transform'
                                                        },
                                                        children: [
                                                            /*#__PURE__*/ _jsx("div", {
                                                                className: "px-4 flex items-center",
                                                                "aria-hidden": "true",
                                                                children: /*#__PURE__*/ _jsx("span", {
                                                                    className: "w-px h-5 bg-muted-foreground",
                                                                    role: "presentation"
                                                                })
                                                            }),
                                                            /*#__PURE__*/ _jsx(AppBreadcrumb, {})
                                                        ]
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: "flex px-6 items-center gap-4",
                                                style: {
                                                    transform: 'translate3d(0, var(--translate-y), 0)',
                                                    willChange: 'transform'
                                                },
                                                children: [
                                                    /*#__PURE__*/ _jsx("div", {
                                                        className: "min-w-52",
                                                        children: /*#__PURE__*/ _jsx(SidebarSearch, {
                                                            ref: searchButtonRef,
                                                            onSearchClick: ()=>{
                                                                setSearchModalOpen(true);
                                                            }
                                                        })
                                                    }),
                                                    /*#__PURE__*/ _jsxs("div", {
                                                        className: "flex gap-4",
                                                        children: [
                                                            /*#__PURE__*/ _jsx(FeedbackButton, {}),
                                                            /*#__PURE__*/ _jsx(HeaderHelpMenu, {}),
                                                            /*#__PURE__*/ _jsx(HeaderUserMenu, {})
                                                        ]
                                                    })
                                                ]
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "bg-sidebar flex items-end border-b border-border",
                                        style: {
                                            height: 'var(--header-row-2-height)',
                                            transform: 'translate3d(0, var(--translate-y), 0)',
                                            willChange: 'transform'
                                        },
                                        children: /*#__PURE__*/ _jsxs("div", {
                                            className: "flex pt-1.5 px-6 h-full items-center gap-3",
                                            style: {
                                                transform: 'translate3d(var(--translate-x), 0, 0)',
                                                willChange: 'transform'
                                            },
                                            children: [
                                                /*#__PURE__*/ _jsx(SidebarTrigger, {}),
                                                /*#__PURE__*/ _jsx(Header, {
                                                    navigationItems: navigationItems
                                                })
                                            ]
                                        })
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex w-full min-w-0",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "hidden lg:block",
                                children: /*#__PURE__*/ _jsx(AppSidebar, {
                                    className: "lg:flex"
                                })
                            }),
                            /*#__PURE__*/ _jsx("main", {
                                className: "flex flex-1 min-w-0 flex-col h-full bg-background p-4",
                                style: {
                                    paddingTop: 'calc(var(--header-height) - 16px)'
                                },
                                children: /*#__PURE__*/ _jsx("div", {
                                    className: "flex flex-1 min-w-0 flex-col",
                                    children: children
                                })
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(SearchModal, {
                        open: searchModalOpen,
                        onOpenChange: setSearchModalOpen
                    }),
                    /*#__PURE__*/ _jsx(MobileMenu, {
                        isOpen: mobileMenuOpen,
                        onOpenChange: setMobileMenuOpen,
                        navigationItems: navigationItems,
                        onSearchClick: ()=>{
                            setSearchModalOpen(true);
                        }
                    })
                ]
            })
        })
    });
};
export { Layout };
